import { Injectable, Injector } from '@angular/core';
import {ExtendedHttpResponse, HttpInterceptorService} from '../../core/services/http-interceptor.service';
import { Observable } from 'rxjs/Observable';
import { filter, map, retry, take } from 'rxjs/operators';
import { IWorkflowRequest } from '../interfaces/workflow-request.interface';
import { IResourcesMap } from '../interfaces/resource-map.interface';
import { IRevision } from '@ispm/workflow/interfaces/revision.interface';
import { DateHandler } from '@ispm/shared/utils/DateHandler';
import { BrowserCacheService } from '@ispm/core/services/browser-cache.service';
import { IgenomicsState } from '@ispm/core/interfaces/genomics-state.interface';
import { DiagnosticOrderActionsService } from '@ispm/dashboard/test/services/diagnostic-order-actions.service';
import { ModalLoaderNgjsService } from '@ispm/layout/services/modal-loader-ngjs.service';
import { CoreActionCreatorService } from '@ispm/core/services/core-action-creator.service';
import { Store } from '@ngrx/store';
import { selectActiveWorkflow } from '@ispm/workflow/store/workflow-config.selectors';
import { isObservable } from 'rxjs';
import { AuthenticationService } from "@ispm/user/services/authentication.service";
import { of } from 'rxjs';
import { isEmpty } from 'lodash';
import {NavigationHelperService} from "@ispm/core/services/navigation-helper.service";

@Injectable()
export class WorkflowService {
  static readonly BASE_URI = '/workflow/';

  public currentTest: any;
  public currentWorkflow: any;
  public onGoingCalls: any = { };
  public webConfig: any;
  public webConfigReq: Promise<any>;
  public wfPromise: any;
  public CURRENT_ORDER_ACTIONS = {
    'INIT_CURRENT_ORDER_STATE': 'CURRENT_ORDER_INIT_CURRENT_ORDER_STATE',
    'SET_PATIENT_ID': 'CURRENT_ORDER_SET_PATIENT_ID',
    'SET_TEST_ID': 'CURRENT_ORDER_SET_TEST_ID',
    'SET_CURRENT_ORDER': 'CURRENT_ORDER_SET_CURRENT_ORDER',
    'SET_ALL_ORDERS': 'CURRENT_ORDER_SET_ALL_ORDERS',
    'SET_ORDERS_COUNT': 'CURRENT_ORDER_SET_ORDERS_COUNT',
    'SET_IS_NEW_ORDER': 'CURRENT_ORDER_SET_IS_NEW_ORDER',
    'SET_SKIP_NOTES_FETCH': 'CURRENT_ORDER_SKIP_NOTES_FETCH',
    'SET_PATIENT': 'CURRENT_ORDER_SET_PATIENT',
    'SET_RESOURCE_MAP': 'CURRENT_ORDER_SET_RESOURCE_MAP',
    'SET_QC_RESULTS': 'CURRENT_ORDER_SET_QC_RESULTS',
    'SET_REVISION_ID': 'CURRENT_ORDER_SET_REVISION_ID',
    'UPDATE_FIELDS': 'CURRENT_ORDER_UPDATE_FIELDS',
    'SET_WORKFLOW_DATA': 'CURRENT_ORDER_SET_WORKFLOW_DATA',
    'SET_TEST_DATA': 'CURRENT_ORDER_SET_TEST_DATA'
  };
  data: any;

  constructor(
    private _http: HttpInterceptorService,
    private _browserCacheService: BrowserCacheService,
    private store$: Store<IgenomicsState>,
    private _DiagnosticOrderActionsService: DiagnosticOrderActionsService,
    private _ModalLoader: ModalLoaderNgjsService,
    private _CoreActionCreatorService: CoreActionCreatorService,
    protected injector: Injector,
    private authService: AuthenticationService
  ) {
  }

  public getWorkflowJobTemplate(id: string, toggleLoader?: boolean): Observable<IRevision> {
    const params = {
      cacheId: this._browserCacheService.getCacheId()
    };

    return this._http.get(`${WorkflowService.BASE_URI}jobTemplate/${id}`, params, toggleLoader)
      .pipe(
        map(res => res.json())
      );
  }

  public getRevisionOpinions(revisions: Array<{ revision: IRevision, item: { [prop: string]: any } }>, sortParamName?: string, sortParamValue?: string): Observable<Array<any>> {
    const ids: string[] = revisions.map(rev => rev.revision.id);
    let params = { revisionId: ids.join() };

    if (sortParamName && sortParamValue) {
      params[sortParamName] = sortParamValue;
    }

    return this._http.get(`${WorkflowService.BASE_URI}getOpinions`, params)
      .map(res => res.json())
      .catch(() => {
        return Observable.empty();
      });
  }

  public getRevisionResourceMap(revisionId: string,additionalPayload = {}): Observable<IResourcesMap> {
    if((!isEmpty(additionalPayload)) && (revisionId === additionalPayload['requestedRevision'])){
       return of(additionalPayload)
    }
    return this._http.get(`${WorkflowService.BASE_URI}resourceMap/${revisionId}`)
      .pipe(
        retry(3),
        map(res => res.json())
      );
  }

  public getWorkflowRequest(workflowId: string, revisionId?: string): Observable<IWorkflowRequest> {
    return this._http.get<IWorkflowRequest>(`${WorkflowService.BASE_URI}find?id=${workflowId}`)
      .pipe(
        retry(3),
        map(res => res.json()),
        map(workflowRequest => {
          if (revisionId) {
            workflowRequest['viewRevisionId'] = revisionId;
          }

          return workflowRequest;
        })
      );
  }

  public performWorkflowStep(
    analysis: any,
    step: string,
    targetPractitionerId: string,
    revisionId: string,
    workflowId?: any,
    interpretationText?: any,
    inputButtonArgs?: any,
    payload?: any,
    verifyProceedToNextStepAllowed?:any,
    partialData?: any
  ): any {
    return verifyProceedToNextStepAllowed ?  this._DiagnosticOrderActionsService.verifyProceedToNextStepAllowed()
      .toPromise()
      .then(validationResult => {
        if (!validationResult.valid) {
          this._ModalLoader.popupModal(
            this._ModalLoader.ComponentsTypes.confirm,
            {
              title: 'Some of the required fields are missing',
              content: 'You cannot complete the step until you fill all of the required fields'
            },
            'ALERT'
          ).subscribe();
          throw new Error('missing_required_fields');
        }
        return true;
      }) : Promise.resolve(true)
      .then(_ => {
        const date = new Date();
        const timeStrings = DateHandler.getTimeStrings(date);
        const params = { payload };

        params['analysis'] = analysis;
        params['inputButtonArgs'] = inputButtonArgs;
        params['targetPractitioner'] = targetPractitionerId;
        params['targetStep'] = step;
        params['revisionId'] = revisionId;
        params['workflowId'] = workflowId;
        params['interpretationText'] = interpretationText;
        params['clientDate'] = (<any> date).toPlatformFormat();
        params['clientTime'] = timeStrings.time12String;
        params['clientTime24Hours'] = timeStrings.time24String;

        if (partialData) {
          params['partialData'] = partialData;
        }

        return this._http.post(WorkflowService.BASE_URI + 'performWorkflowStep', params).toPromise();
      });
  }

  public getWorkflowRevisionDescendantTypes(revisions: any): Observable<any> {
    return this._http.post(`${WorkflowService.BASE_URI}descendantTypes`, { revisions: revisions.join(',') })
      .pipe(
        map(res => res.json())
      );
  }

  public order(order: any) {
    return this._http.post(WorkflowService.BASE_URI, order);
  }

  public getPathWorkList(viewAssignedToMeTests: boolean, filter: any, userAsParameter?: any): Observable<ExtendedHttpResponse<any>> {
    const params: any = { };

    const user = userAsParameter ? userAsParameter : this.authService.user;

    if (user['role'] === 'GenomicsAdminGroup') {
      viewAssignedToMeTests = false;
      user['organization'] = sessionStorage.getItem('adminSelectOrganizationId');
    }

    params.id = (viewAssignedToMeTests || viewAssignedToMeTests === undefined) ? user['info'].id : user['organization'];

    if (filter) {
      params.filter = filter;
    }

    return this._http.get(WorkflowService.BASE_URI + 'worklist', params);
  }

  public getAboutBoxContent(): Promise<ExtendedHttpResponse<any>> {
    return this._http.get(WorkflowService.BASE_URI + 'aboutBoxContent')
      .pipe(
        map(data => data)
      )
      .toPromise();
  }

  public getDefaultChunkSize(): Promise<ExtendedHttpResponse<any>> {
    return this._http.get(WorkflowService.BASE_URI + 'defaultChunk')
      .pipe(
      map(data => data)
      )
      .toPromise();
  }

  public getDashboardConfig(): Promise<any> {
    if (!this.webConfigReq) {
      this.webConfigReq = this._http.get(WorkflowService.BASE_URI + 'dashboardConfig').toPromise();
    }

    return this.webConfigReq;
  }

  public getTotalTests(reference: string, toggleSpinner = false): any {
    let ordersCountFromStore = -1;
    const currOrderSubs = this.store$.select('currentOrder')
      .subscribe(currentOrderDetails => ordersCountFromStore = currentOrderDetails.ordersCount);

    if (currOrderSubs) {
      currOrderSubs.unsubscribe();
    }

    if (ordersCountFromStore !== -1) {
      return Promise.resolve({ 'body': { 'count': ordersCountFromStore } });
    }

    this.toggleSpinner(toggleSpinner, true);

    return this._http.get(WorkflowService.BASE_URI + 'countPatientAnalysis?reference=' + reference)
      .pipe(
        map((count) => {
          this.toggleSpinner(toggleSpinner, false);

          return count;
        })
      ).toPromise();
  }

  public async getPatientAnalysisAllData(params: any, toggleSpinner = false): Promise<any> {
    const getParams = {
      patientId: params.patientId,
      testId: params.testId,
      revisionId: params.revisionId,
      count: 10000
    };
    getParams['patientId'] = params.patientId;
    getParams['testId'] = params.testId;
    getParams['revisionId'] = params.revisionId;
    getParams['count'] = 10000;
    this.toggleSpinner(toggleSpinner, true);
    let resObj = null;
    let currentOrderDetails = null;
    const currOrderSubs = this.store$.select('currentOrder').subscribe(orderData => currentOrderDetails = orderData);
    if (getParams.patientId === currentOrderDetails.patientId && getParams.testId === currentOrderDetails.testId &&

      currentOrderDetails.patientAnalysis && currentOrderDetails.patientAnalysisById && currentOrderDetails.workflow) {
      resObj = {
        patientAnalysisById: currentOrderDetails.patientAnalysisById,
        patientAnalysis: currentOrderDetails.patientAnalysis,
        ordersCount: currentOrderDetails.ordersCount,
        workflowRequest: currentOrderDetails.workflow
      };

    }
    if (currOrderSubs) {
      currOrderSubs.unsubscribe();
    }
    if (resObj) {
      return Promise.resolve(resObj);
    }
    return await this._http.get(WorkflowService.BASE_URI + 'patientAnalysisAllData', getParams
    ).pipe(map((data) => {
        this.toggleSpinner(toggleSpinner, false);
        this.store$.dispatch({ type: this.CURRENT_ORDER_ACTIONS['SET_PATIENT_ID'], payload: getParams.patientId });
        this.store$.dispatch({ type: this.CURRENT_ORDER_ACTIONS['SET_REVISION_ID'], payload: getParams.revisionId });
        this.store$.dispatch({ type: this.CURRENT_ORDER_ACTIONS['SET_TEST_ID'], payload: getParams.testId });
        this.store$.dispatch({ type: this.CURRENT_ORDER_ACTIONS['SET_ALL_ORDERS'], payload: data.body.additionalData.patientAnalysis });
        this.store$.dispatch({ type: this.CURRENT_ORDER_ACTIONS['SET_CURRENT_ORDER'], payload: data.body.additionalData.patientAnalysisById });
        this.store$.dispatch({ type: this.CURRENT_ORDER_ACTIONS['SET_ORDERS_COUNT'], payload: data.body.additionalData.ordersCount });
        this.store$.dispatch({ type: this.CURRENT_ORDER_ACTIONS['SET_WORKFLOW_DATA'], payload: data.body.additionalData.workflowRequest });
        return data.body.additionalData;
      },
      () => {
        return false;
      }
    )).toPromise()
      .catch(() => {
        return Observable.of({ });
      });
  }

  public patientAnalysisById(activatedRouteSnapshot): Observable<any> {
    return this._http.get(`${WorkflowService.BASE_URI}patientAnalysisById`, {
      count: 10000,
      patientId: activatedRouteSnapshot.patientId,
      revisionId: activatedRouteSnapshot.revisionId,
      testId: activatedRouteSnapshot.testId,
    })
      .pipe(
        map(res => res.json()),
      );
  }

  public getAllPatientTests(params: any) {
    return this._http.get('workflow/patientAnalysis', {
      count: 10000,
      patientId: params.patientId,
      revisionId: params.revisionId,
      testId: params.testId,
    })
      .map((res) => {
        this._DiagnosticOrderActionsService.setAllDiagnosticOrders(res.body.allTests);
        return res;
      });
  }

  // keeping the same signature for backward compatibility
  public getPatientAnalyses(route, byId = false, waitForWorkflow = false, toggleSpinner = false): any {
    this.toggleSpinner(toggleSpinner, true);
    const getParams: any = { };
    getParams.patientId = route.patientId;
    getParams.testId = route.testId;
    getParams.revisionId = route.revisionId;
    getParams.count = 10000;
    let patientAnalysisRes: any;
    let currentOrderDetails: any;
    const currOrderSubs = this.store$.select('currentOrder')
      .subscribe(orderData => currentOrderDetails = orderData);
    if (byId) {
      if (currentOrderDetails.testId === getParams.testId && currentOrderDetails.patientAnalysisById) {
        patientAnalysisRes = currentOrderDetails.patientAnalysisById;
        this.currentTest = patientAnalysisRes.current;
        this._DiagnosticOrderActionsService.setDiagnosticOrder(patientAnalysisRes.current);
      }
    }
    else if (currentOrderDetails.patientId === getParams.patientId && currentOrderDetails.patientAnalysis) {
      patientAnalysisRes = currentOrderDetails.patientAnalysis;
      this._DiagnosticOrderActionsService.setAllDiagnosticOrders(patientAnalysisRes.allTests);
    }
    if (currOrderSubs) {
      currOrderSubs.unsubscribe();
    }
    if (patientAnalysisRes) {
      return Promise.resolve({ 'body': patientAnalysisRes });
    }
    this.toggleSpinner(toggleSpinner, true);
    this.wfPromise = waitForWorkflow ? this.getCurrentActiveWorkflow().toPromise() : Promise.resolve(null);
    return this.wfPromise.then(() => {
      const requestKey = [byId, ...Object.values(getParams)].join('__');
      if (!this.onGoingCalls[requestKey]) {
        this.onGoingCalls[requestKey] = byId
          ? this._http.get(WorkflowService.BASE_URI + 'patientAnalysisById', getParams
          ).map(data => {
            this.currentTest = data.body.current;
            this._DiagnosticOrderActionsService.setDiagnosticOrder(data.body.current);
            this.toggleSpinner(toggleSpinner, false);
            return data;
          }) : this._http.get(WorkflowService.BASE_URI + 'patientAnalysis', getParams)
            .map((res) => {
              this._DiagnosticOrderActionsService.setAllDiagnosticOrders(res.body.allTests);
              this.toggleSpinner(toggleSpinner, false);
              return res;
            });
      }
      return isObservable(this.onGoingCalls[requestKey]) ? this.onGoingCalls[requestKey].toPromise() : this.onGoingCalls[requestKey];
    });
  }

  private getCurrentActiveWorkflow(): Observable<IWorkflowRequest> {
    const activeWorkflow$: Observable<IWorkflowRequest> = this.store$.select(selectActiveWorkflow);
    return activeWorkflow$
      .pipe(
        filter(workflow => !!workflow),
        take(1)
      );
  }

  public toggleSpinner(toggleSpinner: boolean, setLoader: boolean) {
    if (toggleSpinner) {
      this._CoreActionCreatorService.setLoader(setLoader);
    }
  }

  public getFileResource(fileId: string): Observable<ExtendedHttpResponse<any>> {
    const getParams = {
      id: fileId
    };

    return this._http.get(WorkflowService.BASE_URI + 'getFileResource', { params: getParams });
  }

  public getWorklfowById(workflowId: string): Promise<ExtendedHttpResponse<any>> {
    const params = {
      id: workflowId
    };

    return this._http.get(WorkflowService.BASE_URI + 'find', params)
      .pipe(
        map(data => {
          this.currentWorkflow = data.body;

          return data;
        })
      )
      .toPromise();
  }

  public pad(n: number): string {
    return n < 10 ? '0' + n.toString(10) : n.toString(10);
  }

  public getFileName(revisionId: string): string {
    const d = new Date();
    const time = [this.pad(d.getHours()), this.pad(d.getMinutes()), this.pad(d.getSeconds())].join('.');
    const date = [d.getFullYear(), this.pad(d.getDate()), this.pad(d.getMonth() + 1)].join('-');

    return (date.concat('T', time, '_', revisionId)).toString();
  }

  public getTimezoneId(): string {
    return new Date().toTimeString().split(' ')[1];
  }

  private SELECTED_VARIANTS = 'ExportSelectionForMDAMI';
  public exportFileToExcelForMI(xHtmlReportTemplates, taskDispatchTemplates, resources, revisionId, testId, MIReportsNumber) {
    const user = this.authService.getUser();
    const body = {
      exportData: {
        exportSelectedOption: this.SELECTED_VARIANTS,
        performer: user['id'],
        resources: resources,
        taskDispatchTemplateId: taskDispatchTemplates,
        fileName: this.getFileName(revisionId),
        worklistType: 'tests',
        xHtmlExportTemplate: xHtmlReportTemplates,
        timeZoneId: this.getTimezoneId(),
        testId: testId,
        resource: 'test',
        MIReportsNumber: MIReportsNumber
      }
    };

    return this._http.post('/report/exportDataToExcel', body);
  }

  public reviseAnalysis(analysis: any, revisedFromRevisionId: string): Observable<ExtendedHttpResponse<any>> {
    const params = {
      analysis,
      revisedFromRevisionId
    };

    return this._http.post(WorkflowService.BASE_URI + 'reviseAnalysis', params);
  }

  public removeAnalysisFromWorklist(analysis: any, revisionId: string, userId: string): Observable<ExtendedHttpResponse<any>> {
    const params = {
      analysis,
      revisionId,
      userId
    };

    return this._http.post(WorkflowService.BASE_URI + 'removeAnalysisFromWorklist', params);
  }

  public getManual(appId: string, langCode: string, ifuContext: string): Observable<ExtendedHttpResponse<any>> {
    const queryString = 'AppId=' + appId + '&&langCode=' + langCode + '&&ifuContext=' + ifuContext;

    return this._http.get(WorkflowService.BASE_URI + 'manual?' + queryString);
  }

  public getPrivacyNotes(langCode: string): Observable<ExtendedHttpResponse<any>> {
    return this._http.get(WorkflowService.BASE_URI + 'privacyNotes?lang=' + langCode);
  }

  public getReportData(reportId: string): Observable<ExtendedHttpResponse<any>> {
    return this._http.get(WorkflowService.BASE_URI + 'report?reportId=' + reportId);
  }

  public toggleCommonDocumentState(docID: string): Observable<ExtendedHttpResponse<any>> {
    return this._http.put('/reportsAndDocuments/updateCommonDocumentActivity', {documentID: docID, updateField: {field: 'active'}});
  }

  public testHasSecondOpinion(revisionId: string): Observable<ExtendedHttpResponse<any> | boolean> {
    let newTest = false;
    const currOrderSubs = this.store$.select('currentOrder').subscribe(currentOrderDetails => newTest = currentOrderDetails && currentOrderDetails.isNewOrder);

    if (currOrderSubs) {
      currOrderSubs.unsubscribe();
    }

    if (newTest) {
      return Observable.of(true);
    }

    const params = {
      revisionId
    };

    return this._http.get(WorkflowService.BASE_URI + 'getOpinions', params)
      .pipe(
        map(res => res.json())
      );
  }

  public async reassignTest(analysis: any, assignedPractId: string, updateOpinion: boolean = true, autoSelect = false): Promise<any> {
    const user = (await this.authService).user;
    const params = {
      activePractitioner: user.info.id,
      revision: analysis.activeRevision,
      workflow: analysis.workflowrequest_id,
      assignedPractId: assignedPractId,
      updateOpinion: updateOpinion,
      order: this.currentTest,
      autoSelect: autoSelect
    };

    return this._http.post(WorkflowService.BASE_URI + 'reassignTest', params).toPromise();
  }

  public async reassignCurationItem(analysis: any, assignedPractId: string, updateOpinion = true): Promise<any> {
    const curationBaseUrl = '/curation/v1';
    const user = (await this.authService).user;
    const params = {
      activePractitioner: user.info.id,
      revision: analysis.activeRevision,
      workflow: analysis.workflowrequest_id,
      assignedPractId: assignedPractId,
      updateOpinion: updateOpinion,
      curationItemID: analysis.curationItemID,
      type: analysis.type,
      kb: analysis.kb,
      status: analysis.status
    };

    return this._http.post(curationBaseUrl + '/reassignCurationItem', params).toPromise();
  }

  public isMDA(): boolean {
    const revisions = this.currentTest?.workflowRequest?.revisions || [];

    return !!revisions[0]?.items?.find((item: any) => item.name === 'UiManualAberrationCuration')
      ?.inputs?.find((input: any) => input.value === 'universalTherapyMatcher');
  }
}
